"use client";

/**
 * Общий хук для работы со списком шаблонов.
 *
 * Используется в TemplatesModal (галерея для пользователя) и в
 * TemplatesAdminTab (админская вкладка). До этого логика загрузки,
 * обработки ошибок и удаления дублировалась в двух местах.
 *
 * Хук не кэширует между инстансами — каждое использование делает свой
 * GET /api/templates. Это осознанно: шаблоны могут меняться админом в
 * другом месте, и простой кэш давал бы устаревшие данные. Если когда-то
 * потребуется глобальный кэш, можно заменить на context/SWR.
 */

import { useCallback, useEffect, useState } from "react";
import { fetchTemplates, deleteTemplate as apiDeleteTemplate } from "@/lib/api/templates";
import type { Template } from "@/lib/api/templates";

export interface UseTemplatesResult {
  templates: Template[];
  categories: string[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  remove: (t: Template) => Promise<void>;
}

export function useTemplates(autoLoad: boolean = true): UseTemplatesResult {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(autoLoad);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchTemplates()
      .then(d => {
        setTemplates(d.templates);
        setCategories(d.categories);
      })
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  const remove = useCallback(async (t: Template) => {
    await apiDeleteTemplate(t.id);
    reload();
  }, [reload]);

  useEffect(() => {
    if (autoLoad) reload();
  }, [autoLoad, reload]);

  return { templates, categories, loading, error, reload, remove };
}
